// src/pages/diagnosis/DiagnosisComplete.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { DiagnosisAPI, PatientInfo, PrescriptionResponse, VisitResponse } from "../../services/diagnosis.service";
import { SoapAPI, UserInfo } from "../../services/soap.service";
import { db } from "../../db/schema";
import type { CachedClinicRecord } from "../../db/schema";
import { unwrapFromStorage } from "../../db/encryption-bridge";
import type { PatientRecord, VisitRecord, PrescriptionRecord } from "../../db/db.types";
import { actionErrorMessage, isBackendUnreachable, isPermissionDenied } from "../../utils/network";
import { getPatientByDisplayId } from "../../services/patient.service";
import { generateOfflinePrescriptionPdf } from "../../services/offline-pdf.service";
import { DoctorAPI, type DoctorFull, type DoctorListItem } from "../../services/doctor.service";
import { PrescriptionDeliveryAPI } from "../../services/prescription-delivery.service";
import { ApiError } from "../../services/api.errors";
import { isInsufficientCreditsError } from "../../services/credits.service";
import { useWalletBalance } from "../../hooks/useWalletBalance";
import { BuyCreditsModal } from "../../components/credits/BuyCreditsModal";
import { DentalSummary } from "../../components/dental/DentalSummary";

const BLUE_GRADIENT = "bg-gradient-to-br from-blue-500 to-sky-400";

const BASE_STATUS_ROWS = [
  {
    icon: "check",
    title: "Diagnosis Confirmed",
    sub: "Saved to patient record",
    badge: "Confirmed",
    badgeBg: "bg-emerald-50",
    badgeText: "text-emerald-600",
  },
  {
    icon: "check",
    title: "Prescription Sent",
    sub: "Delivered to patient",
    badge: "Sent",
    badgeBg: "bg-indigo-50",
    badgeText: "text-indigo-500",
  },
  {
    icon: "check",
    title: "Lab Orders Scheduled",
    sub: "Tomorrow at 9:00 AM",
    badge: "Scheduled",
    badgeBg: "bg-purple-50",
    badgeText: "text-purple-500",
  },
];


const DiagnosisComplete: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const visitId      = searchParams.get("visit_id")   ?? "";
  const rawPatientId = searchParams.get("patient_id") ?? "";
  const doctorId     = searchParams.get("doctor_id")  ?? "";
  const isPatientDisplayId = rawPatientId.toUpperCase().startsWith("PAT-") && !rawPatientId.includes("?");
  const [patientId, setPatientId] = useState(isPatientDisplayId ? "" : rawPatientId);

  const [patient,       setPatient]       = useState<PatientInfo | null>(null);
  const [doctor,        setDoctor]        = useState<UserInfo | null>(null);
  const [doctorRecord,  setDoctorRecord]  = useState<DoctorFull | DoctorListItem | null>(null);
  const [visit,         setVisit]         = useState<VisitResponse | null>(null);
  const [prescriptions, setPrescriptions] = useState<PrescriptionResponse[]>([]);
  const [clinic,        setClinic]        = useState<CachedClinicRecord | null>(null);
  const [pdfLoading,    setPdfLoading]    = useState(false);
  const [pdfError,      setPdfError]      = useState<string | null>(null);
  const [pdfNotice,     setPdfNotice]     = useState<string | null>(null);
  const [waLoading,     setWaLoading]     = useState(false);
  const [emailLoading,  setEmailLoading]  = useState(false);
  const [pendingDelivery, setPendingDelivery] = useState<"email" | "whatsapp" | null>(null);
  const { gateState: creditsGateState, refetch: refetchWallet } = useWalletBalance();
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  useEffect(() => {
    if (!isPatientDisplayId) return;
    getPatientByDisplayId(rawPatientId)
      .then(p => setPatientId(p.patient_id ?? ""))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPatientDisplayId, rawPatientId]);

  // Clinic is needed by the offline PDF generator. The reference-data cache
  // in security-lifecycle populates db.clinics on signIn, so this read works
  // for both online and offline boots. A user only ever has one clinic in
  // this app, so .toArray()[0] is correct.
  useEffect(() => {
    db.clinics.toArray()
      .then(rows => { if (rows[0]) setClinic(rows[0]); })
      .catch(() => { /* non-fatal — offline PDF will render with placeholder clinic */ });
  }, []);

  useEffect(() => {
    if (!visitId || !patientId) return;
    Promise.all([
      DiagnosisAPI.getPatient(patientId).catch(() => null),
      SoapAPI.getUser(doctorId).catch(() => null),
      DiagnosisAPI.getVisit(visitId).catch(() => null),
      DiagnosisAPI.getPrescriptions(visitId).catch(() => [] as PrescriptionResponse[]),
      DoctorAPI.get(doctorId).catch(() => null),
    ]).then(async ([p, d, v, rx, docRecord]) => {
      if (p) setPatient(p);
      if (d) setDoctor(d);
      if (v) setVisit(v.visit);
      if (rx && rx.length > 0) setPrescriptions(rx);
      else console.warn("DiagnosisComplete: no prescriptions found for visit", visitId);

      let resolvedDoc: DoctorFull | DoctorListItem | null = docRecord;
      const visitDoctorId = v?.visit?.doctor_id;
      if (!resolvedDoc && visitDoctorId) {
        resolvedDoc = await DoctorAPI.get(visitDoctorId).catch(() => null);
      }
      setDoctorRecord(resolvedDoc);
    }).catch(async (err) => {
      console.warn("DiagnosisComplete: offline fallback", err);
      try {
        const [rawPatient, rawVisit, rawRxRows, cachedUserRow] = await Promise.all([
          db.patients.get(patientId).catch(() => undefined),
          db.visits.get(visitId).catch(() => undefined),
          db.prescriptions.where("visit_id").equals(visitId).toArray().catch(() => []),
          db.users.get(doctorId).catch(() => undefined),
        ]);
        // Pull the clinic for whichever doctor we ended up with — needed by
        // the offline PDF header.
        const clinicId = cachedUserRow?.clinic_id;
        if (clinicId) {
          const cachedClinic = await db.clinics.get(clinicId).catch(() => undefined);
          if (cachedClinic) setClinic(cachedClinic);
        }

        if (rawPatient) {
          const p = await unwrapFromStorage<PatientRecord>(rawPatient as unknown as Record<string, unknown>);
          setPatient({ patient_id: p.patient_id, first_name: (p as any).first_name ?? "", last_name: (p as any).last_name ?? "", date_of_birth: (p as any).date_of_birth ?? "", gender: (p as any).gender ?? "", contact_number: p.contact_number } as PatientInfo);
        }
        if (rawVisit) {
          const v = await unwrapFromStorage<VisitRecord>(rawVisit as unknown as Record<string, unknown>);
          setVisit({ visit_id: v.visit_id, patient_id: v.patient_id, doctor_id: v.doctor_id, visit_date: v.visit_date, status: v.status, chief_complaint: (v as any).chief_complaint ?? undefined, notes: (v as any).notes ?? undefined, dental_chart: v.dental_chart ?? null } as VisitResponse);
        }
        if (rawRxRows.length > 0) {
          const rxDecrypted = await Promise.all(rawRxRows.map(r => unwrapFromStorage<PrescriptionRecord>(r as unknown as Record<string, unknown>)));
          setPrescriptions(rxDecrypted.map(r => ({ prescription_id: r.prescription_id, visit_id: r.visit_id, prescribed_by: r.prescribed_by, prescribed_at: r.prescribed_at ?? new Date().toISOString(), status: r.status, notes: r.notes ?? undefined, observation: r.observation ?? undefined, medications: r.medications ?? [] } as PrescriptionResponse)));
        }
        if (cachedUserRow) {
          setDoctor(cachedUserRow);
        } else {
          const all = await db.users.toArray().catch(() => []);
          if (all[0]) setDoctor(all[0]);
        }
      } catch (fallbackErr) {
        console.warn("DiagnosisComplete: Dexie fallback failed", fallbackErr);
      }
    });
  }, [visitId, patientId, doctorId]);

  const errMessage = (e: unknown, fallback: string): string =>
    e instanceof Error ? e.message : fallback;

  const latestRx = prescriptions[0] ?? null;

  // Build status rows dynamically so the follow-up row shows the actual date
  // set by the doctor, not a hardcoded "In 7 days".
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const statusRows = useMemo(() => {
    const rows = [...BASE_STATUS_ROWS];
    const followUpText = latestRx?.follow_up || visit?.follow_up_date;
    if (followUpText) {
      // Format "YYYY-MM-DD" → "15 June 2026" if it looks like a raw date string
      const isRawDate = /^\d{4}-\d{2}-\d{2}$/.test(followUpText);
      const label = isRawDate
        ? new Date(followUpText + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
        : followUpText;
      rows.push({
        icon: "calendar",
        title: "Follow-up scheduled",
        sub: label,
        badge: "Scheduled",
        badgeBg: "bg-cyan-50",
        badgeText: "text-cyan-600",
      });
    }
    return rows;
  }, [visit, latestRx]);

  // Render the prescription PDF locally from IndexedDB data. Used when the
  // backend is unreachable so doctors can still hand patients a printable
  // copy; the result is marked "OFFLINE COPY" so it isn't mistaken for the
  // canonical S3-stored, QR-verifiable PDF the server emits on sync.
  const downloadOfflineCopy = (): boolean => {
    if (!latestRx || !patient || !visit) return false;
    try {
      generateOfflinePrescriptionPdf({
        patient,
        visit,
        prescription: latestRx,
        doctor: (doctor || doctorRecord) ? {
          name: doctorRecord?.name || (doctor ? `${doctor.first_name || ""} ${doctor.last_name || ""}`.trim() : undefined),
          first_name: doctor?.first_name || doctorRecord?.name || "",
          last_name: doctor?.last_name || "",
          specialization: (doctorRecord?.specializations && doctorRecord.specializations.length > 0)
            ? doctorRecord.specializations.join(", ")
            : (doctorRecord?.specialization || doctor?.specialization),
          specializations: doctorRecord?.specializations,
          registration_number: doctorRecord?.registration_number || doctor?.registration_number,
          qualifications: doctorRecord?.qualifications || undefined,
          department: doctorRecord?.department || undefined,
          display_id: doctorRecord?.display_id || undefined,
        } : null,
        clinic,
      });
      setPdfNotice("Downloaded an offline copy — the official PDF will be issued once your device reconnects.");
      return true;
    } catch (err) {
      console.warn("offline PDF render failed", err);
      return false;
    }
  };

  const handleDownloadPdf = async () => {
    if (!latestRx) return;

    setPdfLoading(true);
    setPdfError(null);
    setPdfNotice(null);

    // Offline: fall back to the local jsPDF generator
    if (!navigator.onLine) {
      const ok = downloadOfflineCopy();
      if (!ok) setPdfError("Offline copy unavailable — visit data not yet loaded");
      setPdfLoading(false);
      return;
    }

    try {
      // Always regenerate PDF to pick up latest clinic logo, doctor info, etc.
      const result = await DiagnosisAPI.generatePdf(visitId, latestRx.prescription_id);

      window.open(result.pdf_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      // Refused, not offline: the offline copy would hand over, from cache, the
      // prescription PDF the server just declined to issue.
      if (isPermissionDenied(err)) {
        setPdfError(actionErrorMessage(err, "Failed to generate PDF"));
      } else if (isBackendUnreachable(err)) {
        const ok = downloadOfflineCopy();
        if (!ok) setPdfError("PDF generation requires a connection — go online to download");
      } else {
        setPdfError(errMessage(err, "Failed to generate PDF"));
      }
    } finally {
      setPdfLoading(false);
    }
  };

  // Finalise (Draft → Active) before sending. Irreversible — generates S3 PDF.
  // Called only after the doctor confirms the prompt.
  const ensurePrescriptionFinalised = async () => {
    if (!latestRx) return;
    if (latestRx.status === "Active") return;
    try {
      const update = await PrescriptionDeliveryAPI.finalise(latestRx.prescription_id);
      setPrescriptions(prev =>
        prev.map(p => (p.prescription_id === update.prescription_id ? { ...p, ...update } : p)),
      );
    } catch (e) {
      if (e instanceof ApiError && e.isConflict) return; // already active server-side
      throw e;
    }
  };

  // Gate: show confirmation modal if prescription is still Draft; otherwise send directly.
  const handleEmailToPatient = () => {
    if (!latestRx) return;
    if (latestRx.status !== "Active") { setPendingDelivery("email"); return; }
    void _doSendEmail();
  };

  const handleWhatsappToPatient = () => {
    if (!latestRx) return;
    if (!navigator.onLine) { setPdfError("WhatsApp delivery requires a connection."); return; }
    if (latestRx.status !== "Active") { setPendingDelivery("whatsapp"); return; }
    void _doSendWhatsapp();
  };

  const handleConfirmFinalise = async () => {
    const channel = pendingDelivery;
    setPendingDelivery(null);
    if (!channel) return;
    try {
      await ensurePrescriptionFinalised();
    } catch (e) {
      setPdfError(errMessage(e, "Failed to finalise prescription"));
      return;
    }
    if (channel === "email") void _doSendEmail();
    else void _doSendWhatsapp();
  };

  const _doSendEmail = async () => {
    if (!latestRx) return;
    setEmailLoading(true);
    setPdfError(null);
    setPdfNotice(null);
    try {
      // PDF already exists from finalization — no need to regenerate
      const record = await PrescriptionDeliveryAPI.sendEmail(latestRx.prescription_id);
      setPdfNotice(`Prescription emailed to ${record.recipient ?? "patient"}.`);
    } catch (e) {
      if (isInsufficientCreditsError(e)) {
        setShowBuyCredits(true);
      } else {
        setPdfError(errMessage(e, "Failed to email prescription"));
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const _doSendWhatsapp = async () => {
    if (!latestRx) return;
    setWaLoading(true);
    setPdfError(null);
    setPdfNotice(null);
    try {
      const record = await PrescriptionDeliveryAPI.sendWhatsapp(latestRx.prescription_id);
      if (record.status === "sent" || record.status === "pending") {
        setPdfNotice(
          record.recipient
            ? `WhatsApp sent to ${record.recipient}.`
            : "WhatsApp delivery queued.",
        );
      } else {
        setPdfError(record.error_reason || "WhatsApp delivery failed.");
      }
    } catch (e) {
      if (isInsufficientCreditsError(e)) {
        setShowBuyCredits(true);
      } else {
        setPdfError(errMessage(e, "Failed to send WhatsApp"));
      }
    } finally {
      setWaLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const patientName = patient ? `${patient.first_name} ${patient.last_name}` : "—";

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Sidebar />

      {/* Finalise confirmation — shown when doctor clicks Email/WhatsApp on a Draft prescription */}
      {pendingDelivery && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-base font-semibold text-slate-800">Finalise prescription?</p>
              <p className="text-sm text-slate-500">
                Sending via {pendingDelivery === "email" ? "email" : "WhatsApp"} requires finalising
                the prescription first. This locks it and uploads a PDF — it cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingDelivery(null)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmFinalise}
                className="flex-1 py-2.5 rounded-xl bg-teal-600 text-sm font-medium text-white hover:bg-teal-700 transition-colors"
              >
                Finalise &amp; Send
              </button>
            </div>
          </div>
        </div>
      )}

      {showBuyCredits && (
        <BuyCreditsModal onClose={() => setShowBuyCredits(false)} onPurchased={refetchWallet} />
      )}

      <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out min-h-screen flex flex-col p-2 sm:p-3">
        <div className="flex flex-col items-center px-4 sm:px-10 pt-14 pb-6 lg:pt-8 gap-6 w-full">

          {/* ── Header ── */}
          <div className="flex flex-col items-center gap-5 w-full">
            {/* Green checkmark circle */}
            <div className="w-24 h-24 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
                stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            {/* Title */}
            <div className="text-center flex flex-col gap-1.5">
              <h1 className="text-2xl font-bold text-slate-900">Diagnosis Complete!</h1>
              <p className="text-sm text-slate-500">
                All documents have been generated and sent successfully
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 w-full mt-1">
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                All Appointments / Dashboard
              </button>

              <button
                onClick={() => navigate("/patients")}
                className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl ${BLUE_GRADIENT} text-white font-semibold text-sm shadow-md hover:opacity-90 transition-opacity`}
              >
                Start Next Consultation
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </div>
          </div>

          {/* Dental findings + plan, as they printed on the prescription */}
          {visit?.dental_chart && (
            <div className="w-full">
              <DentalSummary chart={visit.dental_chart} />
            </div>
          )}

          {/* ── Action Buttons Grid ── */}
          {pdfError && (
            <p className="w-full text-sm text-red-500 text-center">{pdfError}</p>
          )}
          {pdfNotice && !pdfError && (
            <p className="w-full text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-center">
              {pdfNotice}
            </p>
          )}
          {latestRx && latestRx.status !== "Active" && !pdfError && (
            <p className="w-full text-sm text-slate-500 text-center">
              Finalise the prescription to email or WhatsApp it to the patient.
            </p>
          )}
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Download Prescription PDF */}
            <button
              onClick={handleDownloadPdf}
              disabled={pdfLoading || !latestRx}
              className="flex flex-col items-center gap-2 py-5 px-4 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pdfLoading ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className="animate-spin">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
              <span className="text-sm font-medium text-slate-800">
                {pdfLoading ? "Generating…" : "Download Prescription"}
              </span>
            </button>

            {/* Email to Patient */}
            <button
              onClick={handleEmailToPatient}
              disabled={emailLoading || !latestRx || latestRx?.status !== "Active" || creditsGateState === "zero"}
              title={creditsGateState === "zero" ? "Out of notification credits — buy more to send" : undefined}
              className="flex flex-col items-center gap-2 py-5 px-4 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {emailLoading ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className="animate-spin">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              )}
              <span className="text-sm font-medium text-slate-800">
                {emailLoading ? "Sending…" : "Email to Patient"}
              </span>
            </button>

            {/* WhatsApp to Patient — MSG91 delivery */}
            <button
              onClick={handleWhatsappToPatient}
              disabled={waLoading || !latestRx || latestRx?.status !== "Active" || creditsGateState === "zero"}
              title={creditsGateState === "zero" ? "Out of notification credits — buy more to send" : undefined}
              className="flex flex-col items-center gap-2 py-5 px-4 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {waLoading ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke="#25D366" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className="animate-spin">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke="#25D366" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
              )}
              <span className="text-sm font-medium text-slate-800">
                {waLoading ? "Sending…" : "WhatsApp to Patient"}
              </span>
            </button>
          </div>

          {creditsGateState === "zero" && (
            <button
              type="button"
              onClick={() => setShowBuyCredits(true)}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 -mt-2"
            >
              Out of notification credits — buy more →
            </button>
          )}

          {/* ── Footer note ── */}
          <p className="text-xs text-slate-400 pb-2">
            All documents are automatically saved to patient records
          </p>

        </div>
      </div>
    </div>
  );
};

export default DiagnosisComplete;
